/**
 * Выпадашка, которую не обрезают родители и не перекрывают соседи.
 *
 * Панель с position: absolute режется overflow родителя (так скругляют
 * стеклянные карточки и скроллят тела модалок), а родитель с backdrop-filter
 * или transform запирает её z-index в своём слое — следующая карточка ложится
 * поверх, какой z-index ни дай. Поэтому панель рендерится порталом в body,
 * position: fixed, по прямоугольнику якоря: с предпочтительной стороны, а если
 * там не помещается — с другой, и с зажимом в окне. Позиция пересчитывается
 * каждый кадр, пока панель открыта: так она едет за якорем при прокрутке любого
 * контейнера, ресайзе и сдвигах вёрстки.
 *
 * На прежнем месте панели в дереве остаётся скрытая метка. От неё панель берёт
 * наследуемую типографику (шрифт, кегль, цвет) и видимость, а containsTarget()
 * через неё считает клик по панели кликом внутри контейнера, где панель
 * объявлена, — на этом держится «закрыть по клику снаружи» и у самой
 * выпадашки, и у модалок вокруг неё.
 */
import {
  forwardRef,
  useLayoutEffect,
  useRef,
  type HTMLAttributes,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';

/** Слой выпадашек — над модалками приложения (самая высокая из них — 10000). */
export const FLOATING_Z_INDEX = 10050;

/** Отступ панели от краёв окна, px. */
const VIEWPORT_MARGIN = 8;

/** Наследуемые свойства, которые панель забирает с прежнего места в дереве. */
const INHERITED_PROPS = [
  'color',
  'cursor',
  'fontFamily',
  'fontSize',
  'fontStyle',
  'fontVariantNumeric',
  'fontWeight',
  'letterSpacing',
  'textAlign',
  'textTransform',
  'whiteSpace',
] as const;

/** Слой панели → метка на её прежнем месте в дереве. */
const panelMarkers = new WeakMap<Element, Element>();

/**
 * Лежит ли target внутри container — по DOM или внутри выпадашки, объявленной
 * в container (в том числе вложенной в другую выпадашку). Замена
 * `container.contains(target)` в обработчиках «клика снаружи».
 */
export function containsTarget(
  container: Node | null | undefined,
  target: EventTarget | null
): boolean {
  if (!container) {
    return false;
  }
  let node = target instanceof Node ? target : null;
  while (node) {
    if (container.contains(node)) {
      return true;
    }
    const element = node instanceof Element ? node : node.parentElement;
    const layer = element?.closest('[data-floating-panel]');
    node = layer ? (panelMarkers.get(layer) ?? null) : null;
  }
  return false;
}

export type FloatingPanelProps = HTMLAttributes<HTMLDivElement> & {
  /** Элемент, к которому привязана панель. */
  anchorRef: RefObject<HTMLElement | null>;
  /** С какой стороны якоря открываться, если места хватает. */
  side?: 'bottom' | 'top';
  /** Выравнивание по якорю; stretch — ширина якоря. */
  align?: 'start' | 'center' | 'end' | 'stretch';
  /** Зазор между якорем и панелью, px. */
  sideOffset?: number;
  /** Вместо sideOffset: от верха якоря до верха панели, px, — как `top` у
   *  абсолютной панели внутри якоря, когда его высота не фиксирована. При
   *  перевороте вверх отсчитывается от низа якоря. */
  topOffset?: number;
  /** Сдвиг от выровненного края внутрь якоря, px; у stretch — с обеих сторон. */
  alignOffset?: number;
};

/** Рендерится, пока выпадашка открыта: `{open && <FloatingPanel …>}`. */
export const FloatingPanel = forwardRef<HTMLDivElement, FloatingPanelProps>(
  function FloatingPanel(
    {
      anchorRef,
      side = 'bottom',
      align = 'start',
      sideOffset = 4,
      topOffset,
      alignOffset = 0,
      ...panelProps
    },
    ref
  ) {
    const markerRef = useRef<HTMLSpanElement>(null);
    const layerRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
      const marker = markerRef.current;
      const layer = layerRef.current;
      if (!marker || !layer) {
        return;
      }
      panelMarkers.set(layer, marker);

      const inherited = getComputedStyle(marker);
      for (const prop of INHERITED_PROPS) {
        layer.style[prop] = inherited[prop];
      }
      layer.style.setProperty(
        '-webkit-font-smoothing',
        inherited.getPropertyValue('-webkit-font-smoothing')
      );
      // Высоту строки — множителем: px от метки исказил бы строки другого кегля.
      const fontSize = parseFloat(inherited.fontSize);
      const lineHeight = parseFloat(inherited.lineHeight);
      if (inherited.lineHeight === 'normal') {
        layer.style.lineHeight = 'normal';
      } else if (fontSize > 0 && lineHeight > 0) {
        layer.style.lineHeight = String(lineHeight / fontSize);
      }

      const setStyle = (
        prop: 'visibility' | 'transform' | 'width' | 'top' | 'left',
        value: string
      ) => {
        if (layer.style[prop] !== value) {
          layer.style[prop] = value;
        }
      };
      // В окне с отступом; не влезает целиком — прижимается к верху/левому краю.
      const clamp = (value: number, size: number, viewport: number) =>
        `${Math.max(VIEWPORT_MARGIN, Math.min(value, viewport - VIEWPORT_MARGIN - size))}px`;

      const place = () => {
        const anchor = anchorRef.current;
        // Якорь-предок получает ref уже после эффектов панели — встанем в
        // ближайшем кадре, до отрисовки. Прятать тут нельзя: погас бы autoFocus.
        if (!anchor) {
          return;
        }
        // Якорь убран из вёрстки или спрятан (фоновый слой под оверлеем
        // страницы прячется через visibility) — прячется и панель.
        const hidden =
          anchor.getClientRects().length === 0 ||
          getComputedStyle(marker).visibility === 'hidden';
        setStyle('visibility', hidden ? 'hidden' : '');
        if (hidden) {
          return;
        }

        const rect = anchor.getBoundingClientRect();
        const viewportWidth = document.documentElement.clientWidth;
        const viewportHeight = document.documentElement.clientHeight;

        // Якорь на холсте с transform: scale (макет, вписанный в экран) —
        // панель масштабируется так же, как стояла бы на холсте. Расхождение
        // в пиксель — округление offsetWidth, не масштаб.
        const scale =
          anchor.offsetWidth > 0 &&
          Math.abs(rect.width - anchor.offsetWidth) > 1
            ? rect.width / anchor.offsetWidth
            : 1;
        setStyle('transform', scale === 1 ? '' : `scale(${scale})`);
        const gap =
          topOffset === undefined
            ? sideOffset * scale
            : topOffset * scale - rect.height;
        const inset = alignOffset * scale;

        setStyle(
          'width',
          align === 'stretch'
            ? `${Math.max(0, (rect.width - inset * 2) / scale)}px`
            : 'max-content'
        );
        // scroll-размеры: у stretch панель с min-width шире слоя и вылезает из него.
        const width = Math.max(layer.offsetWidth, layer.scrollWidth) * scale;
        const height = Math.max(layer.offsetHeight, layer.scrollHeight) * scale;

        const roomBelow = viewportHeight - VIEWPORT_MARGIN - rect.bottom - gap;
        const roomAbove = rect.top - gap - VIEWPORT_MARGIN;
        const [preferred, other] =
          side === 'bottom' ? [roomBelow, roomAbove] : [roomAbove, roomBelow];
        const flip = preferred < height && other > preferred;
        const below = (side === 'bottom') !== flip;
        const top = below ? rect.bottom + gap : rect.top - gap - height;
        setStyle('top', clamp(top, height, viewportHeight));

        const left =
          align === 'end'
            ? rect.right - inset - width
            : align === 'center'
              ? rect.left + (rect.width - width) / 2
              : rect.left + inset;
        setStyle('left', clamp(left, width, viewportWidth));
      };

      let frame = 0;
      const loop = () => {
        place();
        frame = requestAnimationFrame(loop);
      };
      loop();

      return () => {
        cancelAnimationFrame(frame);
        panelMarkers.delete(layer);
      };
    }, [anchorRef, side, align, sideOffset, topOffset, alignOffset]);

    return (
      <>
        <span ref={markerRef} hidden />
        {createPortal(
          <div
            ref={layerRef}
            data-floating-panel=""
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              zIndex: FLOATING_Z_INDEX,
              transformOrigin: '0 0',
              width: 'max-content',
              maxWidth: `calc(100vw - ${VIEWPORT_MARGIN * 2}px)`,
              pointerEvents: 'auto',
            }}
          >
            <div ref={ref} {...panelProps} />
          </div>,
          document.body
        )}
      </>
    );
  }
);
